import {
  ControlCode,
  ControlCodes,
  controlOptions,
  createRecordFromArray,
  Detection,
  formatFileName,
  getControlName,
  ThreatHunt,
} from "@theprelude/core";
import {
  useCollapsible,
  Collapsible,
  CollapsibleTrigger,
  RotatingChevron,
  CollapsibleContent,
  RestartIcon,
  Button,
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  TrashcanIcon,
  Input,
  InputField,
  Dropdown,
  DropdownContent,
  DropdownTrigger,
  IconButton,
  DropdownRadio,
  DropdownRadioGroup,
} from "@theprelude/ds";
import { useState } from "react";
import YAML from "yaml";
import { usePopper } from "@/hooks/use-popper";
import { ThreatHuntDeepLink } from "../deep-links";
import { DefaultDialog } from "../default-dialog";
import Editor from "../editor/editor";
import {
  EditableDetection,
  EditableThreatHunt,
} from "./controlled-test-editor";

export function ViewDetections({ detections }: { detections: Detection[] }) {
  return (
    <div className="flex flex-col gap-4 px-4">
      {detections.map((d, index) => (
        <ViewDetection detection={d} index={index} key={index} />
      ))}
    </div>
  );
}

function ViewDetection({
  detection,
  index,
}: {
  detection: Detection;
  index: number;
}) {
  const collapsible = useCollapsible(index === 0 ? true : false);
  return (
    <Collapsible {...collapsible}>
      <CollapsibleTrigger>
        <div className="flex items-center gap-2">
          <IconButton icon={<RotatingChevron active={collapsible.open} />} />
          <span className="text-xl">{detection.name}</span>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-4">
        <div className="pt-4">
          <Editor
            value={YAML.stringify(detection.rule)}
            type="yaml"
            filename={`${formatFileName(detection.name)}.yml`}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function ViewThreatHunts({
  threatHunts,
}: {
  threatHunts: ThreatHunt[];
}) {
  return (
    <div className="flex flex-col gap-4 px-4">
      {threatHunts.map((t, index) => (
        <ViewThreatHunt threatHunt={t} index={index} key={index} />
      ))}
    </div>
  );
}

function ViewThreatHunt({
  threatHunt,
  index,
}: {
  threatHunt: ThreatHunt;
  index: number;
}) {
  const collapsible = useCollapsible(index === 0 ? true : false);
  return (
    <Collapsible {...collapsible}>
      <div className="flex items-center justify-between">
        <CollapsibleTrigger>
          <div className="flex items-center gap-2">
            <IconButton icon={<RotatingChevron active={collapsible.open} />} />
            <span className="text-xl">{threatHunt.name}</span>
          </div>
        </CollapsibleTrigger>
        <ThreatHuntDeepLink
          control={threatHunt.control}
          query={threatHunt.query}
        />
      </div>

      <CollapsibleContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 pt-4">
          <Editor
            value={threatHunt.query}
            filename={`${formatFileName(threatHunt.name)}.txt`}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function EditDetections({
  detections,
  originalDetections,
  updateFn,
  removeFn,
}: {
  detections: EditableDetection[];
  originalDetections: EditableDetection[];
  updateFn: (value: string, id: string, revert?: boolean) => void;
  removeFn: (id: string) => void;
}) {
  const originalDetectionsById = createRecordFromArray(
    originalDetections,
    "id",
  );
  return (
    <div className="flex flex-col gap-4">
      {detections.map((detection, index) => (
        <EditDetection
          detection={detection}
          index={index}
          key={index}
          originalDetection={originalDetectionsById[detection.id]}
          updateFn={updateFn}
          removeFn={removeFn}
        />
      ))}
    </div>
  );
}

function EditDetection({
  detection,
  originalDetection,
  index,
  updateFn,
  removeFn,
}: {
  detection: EditableDetection;
  originalDetection: EditableDetection | undefined;
  index: number;
  updateFn: (value: string, id: string, revert?: boolean) => void;
  removeFn: (id: string) => void;
}) {
  const [removeEntry, setRemoveEntry] = useState<boolean>(false);
  const collapsible = useCollapsible(index === 0 ? true : false);
  return (
    <>
      <RemoveEntryModal
        type="detection"
        index={index}
        open={removeEntry}
        onOpenChange={setRemoveEntry}
        deleteFn={() => removeFn(detection.id)}
      />
      <Collapsible {...collapsible}>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
          <CollapsibleTrigger>
            <div className="flex items-center gap-2">
              <IconButton
                icon={<RotatingChevron active={collapsible.open} />}
              />
              <span className="text-xl">
                {detection.name ?? `New Detection ${index + 1}`}
              </span>
            </div>
          </CollapsibleTrigger>
          <Button
            type="button"
            intent="secondary"
            icon={<TrashcanIcon />}
            onClick={(ev) => {
              ev.stopPropagation();
              setRemoveEntry(true);
            }}
          >
            Delete Detection
          </Button>
        </div>
        <CollapsibleContent className="flex flex-col gap-4">
          <Editor
            filename={`${detection.name ?? `new_detection_${index + 1}`}.yml`}
            editable={true}
            onChange={(value) => updateFn(value, detection.id)}
            value={detection.rule}
            type="yaml"
            height="400px"
            showName={false}
          />
          {originalDetection && (
            <div className="flex items-center justify-end">
              <Button
                type="button"
                icon={<RestartIcon />}
                intent="secondary"
                onClick={() => {
                  updateFn(originalDetection.rule, detection.id, true);
                }}
                disabled={originalDetection === detection}
              >
                Revert changes
              </Button>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </>
  );
}

function RemoveEntryModal(props: {
  type: "threatHunt" | "detection";
  index: number;
  open: boolean;
  onOpenChange: (value: boolean) => any;
  deleteFn: () => void;
}) {
  return (
    <DefaultDialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogHeader>
        <DialogTitle>
          Are you sure you want to remove this{" "}
          {props.type === "detection" ? "Detection" : "Threat Hunt"}?
        </DialogTitle>
      </DialogHeader>
      <DialogBody></DialogBody>
      <DialogFooter>
        <DialogClose asChild>
          <Button type="button" intent="secondary">
            Cancel
          </Button>
        </DialogClose>
        <Button
          type="button"
          intent="negative"
          icon={<TrashcanIcon />}
          onClick={() => {
            void props.deleteFn();
            void props.onOpenChange(false);
          }}
        >
          Remove {props.type === "detection" ? "Detection" : "Threat Hunt"}
        </Button>
      </DialogFooter>
    </DefaultDialog>
  );
}

export function EditThreatHunts({
  threatHunts,
  originalThreatHunts,
  updateFn,
  removeFn,
}: {
  threatHunts: EditableThreatHunt[];
  originalThreatHunts: EditableThreatHunt[];
  updateFn: (
    property: "name" | "query" | "control",
    value: string | ControlCode,
    id: string,
    revert?: boolean,
  ) => void;
  removeFn: (id: string) => void;
}) {
  const originalThreatHuntsById = createRecordFromArray(
    originalThreatHunts,
    "id",
  );
  return (
    <div className="flex flex-col gap-4">
      {threatHunts.map((threatHunt, index) => (
        <EditThreatHunt
          threatHunt={threatHunt}
          index={index}
          key={index}
          originalThreatHunt={originalThreatHuntsById[threatHunt.id]}
          updateFn={updateFn}
          removeFn={removeFn}
        />
      ))}
    </div>
  );
}

function EditThreatHunt({
  threatHunt,
  originalThreatHunt,
  index,
  updateFn,
  removeFn,
}: {
  threatHunt: EditableThreatHunt;
  originalThreatHunt: EditableThreatHunt | undefined;
  index: number;
  updateFn: (
    property: "name" | "query" | "control",
    value: string | ControlCode,
    id: string,
    revert?: boolean,
  ) => void;
  removeFn: (id: string) => void;
}) {
  const [removeEntry, setRemoveEntry] = useState<boolean>(false);
  const collapsible = useCollapsible(index === 0 ? true : false);
  const controlDropdown = usePopper();
  return (
    <>
      <RemoveEntryModal
        type="threatHunt"
        index={index}
        open={removeEntry}
        onOpenChange={setRemoveEntry}
        deleteFn={() => removeFn(threatHunt.id)}
      />
      <Collapsible {...collapsible}>
        <div className="mb-4 flex items-center gap-4">
          <CollapsibleTrigger>
            <IconButton icon={<RotatingChevron active={collapsible.open} />} />
          </CollapsibleTrigger>
          <div className="flex w-full flex-wrap items-end justify-between gap-4">
            <div className="flex flex-wrap items-end gap-4">
              <InputField label="Threat Hunt Title" required>
                <Input
                  type="text"
                  required
                  placeholder={"Enter a name for the query"}
                  value={threatHunt.name}
                  onChange={(ev) =>
                    updateFn("name", ev.target.value, threatHunt.id)
                  }
                  className="w-96 text-sm"
                />
              </InputField>
              <InputField label="ControlCode" required>
                <Dropdown {...controlDropdown}>
                  <DropdownTrigger asChild>
                    <div className="flex h-9 w-48 items-center gap-2 rounded border border-stroke-input bg-surface-background px-3 py-2 text-sm focus:border-primary-foreground focus:outline-none">
                      <div className="flex w-full items-center justify-between">
                        <span className="text-sm">
                          {getControlName(threatHunt.control)}
                        </span>
                        <IconButton
                          icon={<RotatingChevron active={collapsible.open} />}
                        />
                      </div>
                    </div>
                  </DropdownTrigger>
                  <DropdownContent align="start">
                    <DropdownRadioGroup
                      value={String(threatHunt.control)}
                      onValueChange={(value) => {
                        updateFn(
                          "control",
                          Number(value) as ControlCode,
                          threatHunt.id,
                        );
                      }}
                      className="max-h-[300px] w-48 overflow-y-auto"
                    >
                      {controlOptions.map((control) => {
                        // Remove this in future when support for other controls is provided
                        if (
                          control.id !== ControlCodes.CROWDSTRIKE &&
                          control.id !== ControlCodes.DEFENDER
                        ) {
                          return null;
                        }
                        return (
                          <DropdownRadio
                            key={control.name}
                            value={String(control.id)}
                            className={
                              threatHunt.control === control.id
                                ? "bg-tertiary-background"
                                : ""
                            }
                          >
                            {control.name}
                          </DropdownRadio>
                        );
                      })}
                    </DropdownRadioGroup>
                  </DropdownContent>
                </Dropdown>
              </InputField>
            </div>
            <Button
              type="button"
              intent="secondary"
              icon={<TrashcanIcon />}
              onClick={() => {
                setRemoveEntry(true);
              }}
            >
              Delete Threat Hunt
            </Button>
          </div>
        </div>
        <CollapsibleContent className="flex flex-col gap-4">
          <Editor
            filename={formatFileName(threatHunt.name) + ".txt"}
            editable={true}
            value={threatHunt.query}
            onChange={(value) => updateFn("query", value, threatHunt.id)}
            height="400px"
            showName={false}
            type="txt"
          />
          <div className="flex items-center justify-end gap-4">
            <ThreatHuntDeepLink
              control={originalThreatHunt?.control}
              query={threatHunt.query}
            />
            {originalThreatHunt && (
              <Button
                type="button"
                icon={<RestartIcon />}
                intent="secondary"
                onClick={() => {
                  updateFn(
                    "query",
                    originalThreatHunt.query,
                    threatHunt.id,
                    true,
                  );
                }}
                disabled={originalThreatHunt?.query === threatHunt.query}
              >
                Revert changes
              </Button>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </>
  );
}
